/**
 * Static country configuration instances.
 * Single source of truth for both backend and frontend.
 * Backend CountryConfigService wraps these with tenant resolution + caching.
 */
import type { CountryConfig } from './country-config.types';

export const US_CONFIG: CountryConfig = {
  countryCode: 'US',
  countryName: 'United States',
  defaultLanguage: 'en-US',
  defaultTimezone: 'America/New_York',
  dateFormat: 'MM/DD/YYYY',
  numberLocale: 'en-US',
  defaultCurrency: 'USD',
  currencySymbol: '$',
  currencyPosition: 'before',
  unitSystem: 'imperial',
  distanceUnit: 'mi',
  weightUnit: 'lbs',
  volumeUnit: 'ft³',
  temperatureUnit: '°F',
  fuelUnit: 'gal',
  speedUnit: 'mph',
  taxSystem: 'SALES_TAX',
  defaultTaxRate: 0,
  taxIdLabel: 'EIN',
  taxIdFormat: '^\\d{2}-\\d{7}$',
  addressSchema: [
    { field: 'street', label: 'Street Address', required: true, order: 1 },
    { field: 'street2', label: 'Apt/Suite', required: false, order: 2 },
    { field: 'city', label: 'City', required: true, order: 3 },
    { field: 'state', label: 'State', required: true, order: 4 },
    { field: 'postalCode', label: 'ZIP Code', required: true, order: 5, maxLength: 10 },
  ],
  postalCodeFormat: '^\\d{5}(-\\d{4})?$',
  postalCodeLabel: 'ZIP Code',
  stateLabel: 'State',
  phoneCountryCode: '+1',
  phoneFormat: '+1 (XXX) XXX-XXXX',
  phonePlaceholder: '(555) 123-4567',
  complianceRequirements: ['DOT', 'FMCSA', 'ELD', 'IFTA'],
  driverLicenseType: 'CDL',
  vehicleIdType: 'VIN',
  insuranceTypes: ['Liability', 'Cargo', 'Physical Damage'],
  freightPricing: { model: 'NMFC_CLASS', divisor: 139, densityUnit: 'lbs/ft³' },
  maxTruckloadWeight: 19958,
  maxLTLWeight: 9072,
  hosRules: {
    model: 'FMCSA',
    maxDrivingHours: 11,
    maxOnDutyHours: 14,
    requiredOffDutyHours: 10,
    breakAfterHours: 8,
    breakDurationMinutes: 30,
    weeklyLimit7Day: 60,
    weeklyLimit8Day: 70,
    trackingDevice: 'ELD',
  },
  dangerousGoods: {
    model: 'HMR_49CFR',
    segregationTable: 'CFR_177_848',
    tunnelCodes: false,
    markings: 'PLACARDS_172_500',
    transportDocument: 'SHIPPING_PAPER_172_200',
    emergencyResponseInfo: true,
    ergEnabled: true,
    driverCertModel: 'CDL_ENDORSEMENT',
    vehicleApprovalModel: 'DOT_CARGO_TANK',
    carrierAuthorityModel: 'PHMSA_HMSP',
  },
  vehicleTypes: [
    'TRUCK_CLASS_8',
    'TRUCK_CLASS_7',
    'TRUCK_CLASS_6',
    'VAN',
    'TRAILER_53FT',
    'TRAILER_48FT',
  ],
  requiredDocuments: ['DOT_NUMBER', 'MC_NUMBER', 'INSURANCE_CERTIFICATE', 'BOL', 'POD'],
  defaultCargoTypes: ['01', '02', '03', '04', '05', '06', '07', '08'],
  availableIntegrations: ['HERE_MAPS', 'TWILIO_SMS', 'STRIPE_PAYMENTS', 'QUICKBOOKS'],
};

export const TR_CONFIG: CountryConfig = {
  countryCode: 'TR',
  countryName: 'Turkiye',
  defaultLanguage: 'tr-TR',
  defaultTimezone: 'Europe/Istanbul',
  dateFormat: 'DD.MM.YYYY',
  numberLocale: 'tr-TR',
  defaultCurrency: 'TRY',
  currencySymbol: '₺',
  currencyPosition: 'after',
  unitSystem: 'metric',
  distanceUnit: 'km',
  weightUnit: 'kg',
  volumeUnit: 'm³',
  temperatureUnit: '°C',
  fuelUnit: 'L',
  speedUnit: 'km/h',
  taxSystem: 'VAT',
  defaultTaxRate: 20,
  taxIdLabel: 'VKN',
  taxIdFormat: '^\\d{10}$',
  addressSchema: [
    { field: 'street', label: 'Sokak/Cadde', required: true, order: 1 },
    { field: 'buildingNo', label: 'Bina No', required: true, order: 2 },
    { field: 'neighborhood', label: 'Mahalle', required: false, order: 3 },
    { field: 'district', label: 'Ilce', required: true, order: 4 },
    { field: 'state', label: 'Il', required: true, order: 5 },
    { field: 'postalCode', label: 'Posta Kodu', required: false, order: 6, maxLength: 5 },
  ],
  postalCodeFormat: '^\\d{5}$',
  postalCodeLabel: 'Posta Kodu',
  stateLabel: 'Il',
  phoneCountryCode: '+90',
  phoneFormat: '+90 (5XX) XXX XX XX',
  phonePlaceholder: '(532) 123 45 67',
  complianceRequirements: ['GIB', 'U-ETDS', 'e-Irsaliye', 'e-Fatura'],
  driverLicenseType: 'SRC',
  vehicleIdType: 'Plaka',
  insuranceTypes: ['Zorunlu Trafik Sigortasi', 'Kasko', 'Tasiyici Sorumluluk'],
  freightPricing: { model: 'DESI', divisor: 3000, densityUnit: 'kg' },
  maxTruckloadWeight: 20000,
  maxLTLWeight: 9000,
  hosRules: {
    model: 'EU_TACHOGRAPH',
    maxContinuousDrivingHours: 4.5,
    breakDurationMinutes: 45,
    maxDailyDrivingHours: 9,
    maxDailyDrivingExtended: 10,
    maxWeeklyDrivingHours: 56,
    maxBiWeeklyDrivingHours: 90,
    dailyRestHours: 11,
    dailyRestReducedHours: 9,
    weeklyRestHours: 45,
    trackingDevice: 'TACHOGRAPH',
  },
  dangerousGoods: {
    model: 'ADR',
    segregationTable: 'ADR_7_5_2',
    tunnelCodes: true,
    markings: 'ORANGE_PLATE',
    transportDocument: 'ADR_5_4_1',
    writtenInstructionsRequired: true,
    driverCertModel: 'SRC5_ADR',
    vehicleApprovalModel: 'ADR_PART_9',
    carrierAuthorityModel: 'TR_YETKI_TMGD',
    // TR-local summer restriction: Class 3 (flammable liquids) daytime
    // carriage limits above 30°C, June–August (config data, not code)
    seasonalRules: {
      summerHeat: { months: [6, 7, 8], classes: ['3'], maxTempC: 30 },
    },
  },
  vehicleTypes: ['TIR_NORMAL', 'KAMYON', 'KAMYONET', 'DORSE', 'FRIGORIFIK'],
  requiredDocuments: ['K1_BELGESI', 'TIR_KARNESI', 'TASIMA_BELGESI', 'E_IRSALIYE'],
  defaultCargoTypes: ['01', '02', '03', '04', '05', '06', '07', '08'],
  availableIntegrations: ['HERE_MAPS', 'NETGSM_SMS', 'GIB_E_IRSALIYE', 'U_ETDS'],
  weekendRestrictions: {
    vehicleClasses: ['N3', 'TIR', 'KIRKAYAK'],
    startDay: 5,
    startHour: 15,
    endDay: 1,
    endHour: 0,
  },
};

export const COUNTRY_CONFIGS: Record<string, CountryConfig> = {
  US: US_CONFIG,
  TR: TR_CONFIG,
};
